import React from 'react';
import {Alert, Box, Button, LinearProgress, Stack, Tooltip, Typography} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import {ComponentState} from '../../../../../@types/component';
import {
    ComponentMappingStatusSummary,
    ComponentReportMappingState,
    MappingRunMode,
    MappingRunStatus,
} from '../../../../../@types/component-mapping-state';
import {ComponentMappingEditorProps} from '../../../../../@types/editor-props';
import newOccurrenceService from '../../../../../services/new-occurrence.service';
import componentMappingStateService from '../../../../../services/component-mapping-state.service';
import {useComponentMappingRun} from '../../../../../hooks/useComponentMappingRun';
import ErrorAlertComponent from '../../../../../components/ErrorAlert.component';
import IRPWorkbookProvider from '../irp-workbook/IRPWorkbookProvider';
import IRPWorkbookComponent from '../irp-workbook/IRPWorkbook.component';
import ComponentSourceTable from './ComponentSourceTable';
import {reportsDue} from './mapping-status-text';
import {ReportSourceOption, buildSourceRows, describeRunProgress, toCount} from './source-rows';

/** How often the source table re-reads while a run is going. Matches the run hook's own beat. */
const SOURCE_POLL_MS = 5000;

/**
 * Mapping a component version against whole report sources (specification 0.4, section 4.7),
 * opened from the component's action menu as a view of its own. Every source is listed with where
 * this version stands against it; choosing one offers an update or a full re-map, and shows the
 * mappings themselves in the same workbook an IRP uses, filling the rest of the view. Test runs
 * are not here: a draft is tested from its canvas, in the Model Testing dock, and is told so.
 */
const ComponentMappingEditor: React.FC<ComponentMappingEditorProps> = ({component}: ComponentMappingEditorProps) => {
    const isDraft = component.state === ComponentState.DRAFT;
    const isPublished = component.state === ComponentState.PUBLISHED;

    const [sources, setSources] = React.useState<ReportSourceOption[]>([]);
    const [sourceStates, setSourceStates] = React.useState<ComponentReportMappingState[]>([]);
    const [sourceStatuses, setSourceStatuses] = React.useState<ComponentMappingStatusSummary[]>([]);
    const [sourcesError, setSourcesError] = React.useState<string | null>(null);
    const [statesError, setStatesError] = React.useState<string | null>(null);
    const [sourceName, setSourceName] = React.useState<string | null>(null);

    const {runState, mappingStatus, isLoading, error, isActive, startMappingRun, cancelMappingRun} =
        useComponentMappingRun(component.id, sourceName);

    // Matches DescriptorApply, the screen this one is modelled on, which loads its source list the
    // same way. Fetching in an effect is against the repo's React guidance, but the alternative it
    // implies does not exist in this codebase yet.
    React.useEffect(() => {
        let cancelled = false;
        newOccurrenceService.getReportSources()
            .then(list => {
                if (!cancelled) {
                    setSources(list.map(s => ({sourceName: s.sourceName, reportCount: s.reportCount})));
                    setSourcesError(null);
                }
            })
            .catch((err: Error) => {
                if (!cancelled) {
                    setSourcesError(err.message);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // The per-source states and statuses, read once on open and then on a beat for as long as a
    // run is going. A run's activity flips only at its two edges, so reading on that alone would
    // leave the running source's row frozen while the progress bar below it advanced.
    React.useEffect(() => {
        let cancelled = false;
        const read = (): void => {
            Promise.all([
                componentMappingStateService.listSourceStates(component.id),
                componentMappingStateService.listMappingStatuses(component.id),
            ])
                .then(([states, statuses]) => {
                    if (!cancelled) {
                        setSourceStates(states);
                        setSourceStatuses(statuses);
                        setStatesError(null);
                    }
                })
                .catch((err: Error) => {
                    if (!cancelled) {
                        setStatesError(err.message);
                    }
                });
        };
        read();
        const timer = isActive ? window.setInterval(read, SOURCE_POLL_MS) : null;
        return () => {
            cancelled = true;
            if (timer != null) {
                window.clearInterval(timer);
            }
        };
    }, [component.id, isActive]);

    // The run hook reads the selected source's status on its own beat, and re-reads it the moment
    // a run finishes, so its reading is the fresher one. The row takes it, so the table and the
    // controls beneath it never say two different things about the same source.
    const sourceRows = React.useMemo(() => {
        const rows = buildSourceRows(sources, sourceStates, sourceStatuses);
        return mappingStatus
            ? rows.map(row => (row.sourceName === sourceName ? {...row, status: mappingStatus} : row))
            : rows;
    }, [sources, sourceStates, sourceStatuses, mappingStatus, sourceName]);

    // A source with records but no live report count is no longer offered by the report service.
    // Its row stays selectable so its evidence and workbook remain reachable, but a run against it
    // would only fail at the backend, so it is not offered as runnable.
    const selectedRow = sourceRows.find(row => row.sourceName === sourceName) ?? null;
    const sourceIsListed = selectedRow != null && selectedRow.reportCount != null;
    const canRun = isPublished && !isActive && !isLoading && sourceIsListed;
    const due = mappingStatus ? reportsDue(mappingStatus) : null;
    const nothingToUpdate = due === 0;

    const percent = toCount(runState?.runStatistics?.percentComplete) ?? 0;
    const showProgress = isActive || runState?.runStatus === MappingRunStatus.RUNNING;
    const hasMappings = sourceName != null && runState != null;

    return (
        <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Stack spacing={1.5} sx={{p: 2, pb: 1, flexShrink: 0}}>
                <Typography variant="h6">
                    Mapping: {component.name}
                    {component.version ? ` v${component.version}` : ''}
                </Typography>

                {sourcesError && <ErrorAlertComponent message={sourcesError} />}
                {statesError && <ErrorAlertComponent message={statesError} />}

                {isDraft && (
                    <Alert severity="info" sx={{py: 0.5}}>
                        A draft is tested from its canvas, in Model Testing, against single reports or a
                        shakedown sample. Mapping a version against a whole source records evidence that
                        other models will pin, so it is done once the version is published.
                    </Alert>
                )}
                {!isDraft && !isPublished && (
                    <Alert severity="info" sx={{py: 0.5}}>
                        This version is {component.state.toLowerCase()}. Its existing mappings stay readable,
                        but no new run is started against it.
                    </Alert>
                )}

                <ComponentSourceTable
                    rows={sourceRows}
                    selectedSourceName={sourceName}
                    onSelect={(selected): void => {
                        if (!isActive) {
                            setSourceName(selected);
                        }
                    }}
                />

                {selectedRow != null && !sourceIsListed && (
                    <Alert severity="warning" sx={{py: 0.5}}>
                        This source is no longer listed by the report service. Its existing records and
                        workbook stay readable, but it cannot be run against again.
                    </Alert>
                )}

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {sourceName != null && due != null && due > 0 && !isActive && (
                        <Typography variant="body2" color="text.secondary">
                            {sourceName}: {due.toLocaleString()} report{due === 1 ? '' : 's'} still to evaluate
                        </Typography>
                    )}
                    <Box sx={{flexGrow: 1}} />
                    {isPublished && (
                        <>
                            <Tooltip title={nothingToUpdate
                                ? 'Every report from this source has already been evaluated for this version'
                                : 'Evaluate the reports with no record, and those that changed since theirs'}>
                                <span>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<PlayArrowIcon />}
                                        onClick={(): void => { void startMappingRun({mode: MappingRunMode.UPDATE}); }}
                                        disabled={!canRun || nothingToUpdate}
                                    >
                                        Update mapping
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Discard every record for this source and evaluate it all again">
                                <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ReplayIcon />}
                                        onClick={(): void => { void startMappingRun({mode: MappingRunMode.FULL_REMAP}); }}
                                        disabled={!canRun}
                                    >
                                        Full re-map
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {isActive && (
                        <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<StopIcon />}
                            onClick={(): void => { void cancelMappingRun(); }}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                    )}
                </Stack>

                {showProgress && (
                    <Box>
                        <LinearProgress
                            variant={runState?.runStatistics ? 'determinate' : 'indeterminate'}
                            value={Math.min(100, Math.max(0, percent))}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {describeRunProgress(runState) || 'Starting…'}
                        </Typography>
                    </Box>
                )}

                {runState?.runStatus === MappingRunStatus.ERROR && runState.runError && (
                    <ErrorAlertComponent message={runState.runError} />
                )}
                {error && <ErrorAlertComponent message={error} />}
            </Stack>

            {/* The mappings themselves, in the same workbook an IRP uses, filling the rest of the
                view. Keyed by source so each source boots its own workbook store, as the dialog
                that used to hold this did on every opening. */}
            <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderTop: 1, borderColor: 'divider'}}>
                {hasMappings ? (
                    <IRPWorkbookProvider key={sourceName} componentMapping={{componentId: component.id, sourceName: sourceName as string}}>
                        <IRPWorkbookComponent />
                    </IRPWorkbookProvider>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                        {sourceName == null
                            ? 'Choose a source above to see its mappings.'
                            : 'This version has not been run against this source yet.'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default ComponentMappingEditor;
